import { useState, useEffect, useRef } from 'react';
import { Navigation } from '../../components/Navigation';
import { Bot, Send, Sparkles, Search, Bell, TrendingUp, Briefcase, MapPin, Coins } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useCreditsStore } from '../../store/creditsStore';
import {
  aiAssistant,
  AIAssistantMessage,
  // ScrapedJob, // TODO: Add job scraping feature integration
  AISession
} from '../../lib/aiCareerAssistant';
import { SessionManager } from '../../components/SessionManager';
import { FileUpload } from '../../components/FileUpload';

export function AIAssistant() {
  const { user } = useAuthStore();
  const { balance, fetchCredits } = useCreditsStore();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<AISession[]>([]);
  const [messages, setMessages] = useState<AIAssistantMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    initializeSession();
    if (user) {
      fetchCredits(user.id);
    }
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSessions = async () => {
    if (!user) return;
    const userSessions = await aiAssistant.getActiveSessions(user.id);
    setSessions(userSessions);
    return userSessions;
  };

  const initializeSession = async () => {
    if (!user) return;

    const userSessions = await loadSessions();

    if (userSessions && userSessions.length > 0) {
      const latestSession = userSessions[0];
      setSessionId(latestSession.id);
      const sessionMessages = await aiAssistant.getSessionMessages(latestSession.id);
      setMessages(sessionMessages);
    } else {
      await createNewSession();
    }
  };

  const createNewSession = async () => {
    if (!user) return;

    const newSession = await aiAssistant.createSession(user.id, `Chat ${new Date().toLocaleDateString()}`);
    if (newSession) {
      setSessionId(newSession.id);
      const welcomeMessage: AIAssistantMessage = {
        role: 'assistant',
        content: `Hi! I'm your AI Career Assistant. I can help you find jobs by searching across:\n\nMajor Job Boards: Indeed, LinkedIn, Glassdoor, ZipRecruiter, Monster\n\nCompany Career Pages: Direct from employer websites\n\nIndustry-Specific Sites: Nonprofit, tech, education, and more\n\nGovernment Sites: Public sector opportunities\n\nJust tell me what kind of job you're looking for, and I'll search hundreds of sources to find the best matches!`,
      };
      setMessages([welcomeMessage]);
      if (newSession.id) {
        await aiAssistant.addMessage(newSession.id, 'assistant', welcomeMessage.content);
      }
      await loadSessions();
    }
  };

  const handleSelectSession = async (newSessionId: string) => {
    setSessionId(newSessionId);
    const sessionMessages = await aiAssistant.getSessionMessages(newSessionId);
    setMessages(sessionMessages);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !sessionId || !user) return;

    const SEARCH_CREDIT_COST = 1;

    if (balance < SEARCH_CREDIT_COST) {
      const errorMessage: AIAssistantMessage = {
        role: 'assistant',
        content: `You need ${SEARCH_CREDIT_COST} credit to perform a job search. Please purchase more credits to continue using the AI Assistant.`,
      };
      setMessages((prev) => [...prev, errorMessage]);
      return;
    }

    const userMessage: AIAssistantMessage = {
      role: 'user',
      content: inputMessage,
    };

    setMessages((prev) => [...prev, userMessage]);
    await aiAssistant.addMessage(sessionId, 'user', inputMessage);

    const searchQuery = inputMessage;
    setInputMessage('');
    setLoading(true);

    try {
      const criteria = aiAssistant.parseNaturalLanguageQuery(searchQuery);

      const sources = await aiAssistant.getJobSources();
      const results = await aiAssistant.searchJobs(criteria);

      await aiAssistant.logSearchQuery(
        user.id,
        sessionId,
        searchQuery,
        criteria,
        sources.map((s) => s.name),
        results.length
      );

      await useCreditsStore.getState().spendCredits(
        user.id,
        SEARCH_CREDIT_COST,
        'ai_job_search',
        `AI job search: ${searchQuery.substring(0, 50)}`
      );

      await fetchCredits(user.id);

      const response = await aiAssistant.generateResponse(searchQuery, results);

      const assistantMessage: AIAssistantMessage = {
        role: 'assistant',
        content: response,
        metadata: { jobCount: results.length, criteria },
      };

      setMessages((prev) => [...prev, assistantMessage]);
      await aiAssistant.addMessage(sessionId, 'assistant', response, assistantMessage.metadata);
    } catch (error) {
      console.error('Error processing message:', error);
      const errorMessage: AIAssistantMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error while searching for jobs. Please try again.',
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickActions = [
    { label: 'Remote jobs in tech', icon: Briefcase },
    { label: 'Entry level positions', icon: TrendingUp },
    { label: 'Jobs in Detroit, MI', icon: MapPin },
    { label: 'Nonprofit opportunities', icon: Sparkles },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 overflow-hidden flex flex-col h-[calc(100vh-12rem)]">
              <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <Bot className="w-7 h-7" />
                    </div>
                    <div>
                      <h1 className="text-2xl font-bold">AI Career Assistant</h1>
                      <p className="text-white/90 text-sm">
                        Searching across hundreds of job sources
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl">
                    <Coins className="w-5 h-5" />
                    <span className="font-bold text-lg">{balance}</span>
                    <span className="text-sm text-white/90">Credits</span>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.map((message, idx) => (
                  <div
                    key={idx}
                    className={`flex ${
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    {message.role === 'assistant' && (
                      <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mr-3">
                        <Bot className="w-5 h-5 text-white" />
                      </div>
                    )}
                    <div
                      className={`max-w-[75%] rounded-2xl px-5 py-3 ${
                        message.role === 'user'
                          ? 'bg-cyan-500 text-white'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <p className="text-sm leading-relaxed whitespace-pre-line">
                        {message.content}
                      </p>
                      {message.metadata?.jobCount !== undefined && (
                        <div className="mt-2 pt-2 border-t border-gray-300/30 text-xs opacity-75">
                          Found {message.metadata.jobCount} jobs
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mr-3">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div className="bg-gray-100 rounded-2xl px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" />
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              <div className="border-t-2 border-gray-200 p-4">
                {balance < 1 && (
                  <div className="mb-3 px-3 py-2 bg-orange-50 border-2 border-orange-300 rounded-lg text-sm text-orange-800">
                    <strong>Low credits:</strong> You need 1 credit per search. <a href="/credits" className="underline font-semibold">Get more credits</a>
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask me to find jobs... (1 credit per search)"
                    className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-cyan-500 focus:outline-none"
                    disabled={loading}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={loading || !inputMessage.trim()}
                    className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:from-cyan-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {quickActions.map((action, idx) => {
                    const Icon = action.icon;
                    return (
                      <button
                        key={idx}
                        onClick={() => setInputMessage(action.label)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {action.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <FileUpload sessionId={sessionId} onFileUploaded={loadSessions} />
            </div>
          </div>

          <div className="space-y-6">
            <SessionManager
              sessions={sessions}
              currentSessionId={sessionId}
              onSelectSession={handleSelectSession}
              onCreateSession={createNewSession}
              onRefreshSessions={loadSessions}
            />

            <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Search className="w-6 h-6 text-cyan-500" />
                <h2 className="text-xl font-bold text-gray-900">Search Sources</h2>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Major Job Boards</p>
                    <p className="text-xs text-gray-600">
                      Indeed, LinkedIn, Glassdoor, ZipRecruiter, Monster
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Company Pages</p>
                    <p className="text-xs text-gray-600">
                      Direct employer career sites
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Industry Boards</p>
                    <p className="text-xs text-gray-600">
                      Nonprofit, tech, education sectors
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Government Sites</p>
                    <p className="text-xs text-gray-600">USAJobs and public sector</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-6 text-white">
              <Bell className="w-8 h-8 mb-3 opacity-90" />
              <h2 className="text-xl font-bold mb-2">Set Up Job Alerts</h2>
              <p className="text-white/90 text-sm mb-4">
                Get notified when new jobs matching your criteria are posted across all sources.
              </p>
              <button className="w-full py-2.5 bg-white text-orange-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                Create Alert
              </button>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-6 h-6 text-cyan-500" />
                <h2 className="text-xl font-bold text-gray-900">Tips</h2>
              </div>

              <div className="space-y-3 text-sm text-gray-700">
                <p>
                  <strong>Be specific:</strong> "remote software engineer Detroit" works better
                  than just "jobs"
                </p>
                <p>
                  <strong>Include salary:</strong> Try "$80k software engineer" to filter by pay
                </p>
                <p>
                  <strong>Set alerts:</strong> I'll monitor all sources 24/7 and notify you of new
                  matches
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
